import React, { useEffect, useContext, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Grid,
  Paper,
  Typography,
  MenuItem,
  TextField,
  Avatar,
  Chip,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";

import CloudDownloadIcon from "@material-ui/icons/CloudDownload";
import { OAuth as OAuthActions } from "../../js/actions";
import jwt from "jwt-decode";
import TokenStorage from "../../js/utils/tokenStorage";
import { Context } from "../../js/utils/Context";
import { setSnackbar } from "../../js/reducers/snackbar";

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: "calc(100vh - 127px)",
    backgroundColor: "#F8FAFC",
    padding: theme.spacing(4),
    paddingTop: theme.spacing(3),
    fontFamily: "'Inter', 'Plus Jakarta Sans', sans-serif",

    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(3),
    },

    " & .MuiTextField-root": {
      marginTop: "-0.4rem !important",
    },
  },

  userBlock: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
  },

  avatar: {
    width: 44,
    height: 44,
    backgroundColor: "#2563EB",
    color: "#FFFFFF",
    fontSize: 15,
    fontWeight: 800,
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    boxShadow: "0 0 0 3px #DBEAFE",
  },

  welcomeText: {
    fontWeight: 800,
    fontSize: "20px !important",
    color: "#0F172A",
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    lineHeight: 1.2,
  },

  roleChip: {
    marginTop: 4,
    height: 22,
    fontSize: 12,
    fontWeight: 700,
    backgroundColor: "#DBEAFE",
    color: "#2563EB",
    letterSpacing: 0.6,
    textTransform: "uppercase",
  },

  oemSelect: {
    minWidth: 170,
    "& .MuiInputBase-root": {
      margin: "0 !important",
      borderRadius: "14px !important",
      background: "#ffffff !important",
      transition: "box-shadow 0.25s ease, background 0.25s ease",
      "&.Mui-focused": {
        background: "#ffffff !important",
        boxShadow: "0 6px 18px rgba(11, 17, 19, 0.18)",
      },
      fontFamily: "Bosch office Sans",
    },
    "& .MuiOutlinedInput-root": {
      padding: "4px 14px !important",
      "& fieldset": {
        borderColor: "rgba(11, 60, 93, 0.15)",
      },
      "&:hover fieldset": {
        borderColor: "rgba(3, 169, 243, 0.5)",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#03A9F3",
        borderWidth: "1.5px",
      },
    },
    "& .MuiAutocomplete-input": {
      padding: "12.5px 14px 12.5px 2px !important",
      fontSize: "0.90em !important",
      background: "transparent !important",
      color: "#2f3d5f   !important",
      "&::placeholder": {
        color: "#2f3d5f  !important",
        opacity: 1,
      },
      "&:focus": {
        background: "transparent !important",
      },
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: "#0288d1",
    },
  },

  autocompletePaper: {
    borderRadius: "12px",
    marginTop: "5px",
    background: "#ffffff",
    boxShadow: "0 8px 30px rgba(3, 46, 79, 0.18)",
    border: "1px solid rgba(11, 60, 93, 0.08)",
    "& .MuiList-root": {
      padding: "6px",
      background: "#ffffff",
    },
    "& .MuiListItem-gutters": {
      paddingLeft: "5px",
      paddingRight: "5px",
    },
    "& .MuiListItem-root.Mui-focusVisible": {
      backgroundColor: "transparent",
    },
  },

  menuItem: {
    fontSize: "0.90rem",
    lineHeight: 1.4,
    minHeight: "auto",
    paddingTop: "0px",
    paddingBottom: "0px",
    "&.Mui-selected": {
      backgroundColor: "rgba(3, 169, 243, 0.12)",
      "&:hover": {
        backgroundColor: "rgba(3, 169, 243, 0.16)",
      },
    },
  },

  tileLink: {
    textDecoration: "none",
    display: "block",
    height: "100%",
  },

  card: {
    backgroundColor: "#FFFFFF",
    borderRadius: 20,
    overflow: "hidden",
    cursor: "pointer",
    transition: "all 0.22s ease",
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
    height: "100%",

    "&:hover": {
      transform: "translateY(-5px) scale(1.01)",
      boxShadow: "0 20px 48px rgba(0,0,0,0.13)",
    },
  },

  gradientZone: {
    height: 110,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1.4, 2.5, 0, 2.5),
    position: "relative",
  },

  tagPill: {
    fontSize: 10,
    fontWeight: 700,
    color: "rgba(255,255,255,0.85)",
    letterSpacing: 0.8,
    textTransform: "uppercase",
    backgroundColor: "rgba(255,255,255,0.15)",
    padding: "4px 10px",
    borderRadius: 20,
    alignSelf: "flex-start",
    marginTop: 4,
    fontFamily: "Roboto, Helvetica, Arial, sans-serif"
  },

  iconBox: {
    position: "absolute",
    bottom: -22,
    left: 24,
    width: 52,
    height: 52,
    borderRadius: 14,
    backgroundColor: "#FFFFFF",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
  },

  tileIcon: {
    width: 32,
    height: 32,
    objectFit: "contain",
  },

  fallbackIconText: {
    fontSize: 18,
    fontWeight: 800,
    color: "#2563EB",
  },

  bottomContent: {
    padding: theme.spacing(4.3, 2.5, 2.5, 2.5),
  },

  tileTitle: {
    fontWeight: 700,
    fontSize: "14px !important",
    color: "#0F172A",
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    lineHeight: 1.3,
    marginBottom: 6,
  },

  tileDescription: {
    fontSize: "12px !important",
    color: "#94A3B8",
    lineHeight: 1.5,
  },

  // disabledCard: {
  //   opacity: 0.85,
  // },

  downloadContainer: {
    width: "100%",
    marginTop: theme.spacing(8),
    display: "flex",
    justifyContent: "center",
  },

  downloadBox: {
    backgroundColor: "#FFFFFF",
    borderRadius: 20,
    padding: theme.spacing(3),
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    maxWidth: 620,
  },

  downloadIcon: {
    width: 44,
    height: 44,
    color: "#2563EB",
    cursor: "pointer",
  },
}));

const Dashboard = () => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [makeSelected, setMakeSelected] = useContext(Context);
  const [makeList, setMakeList] = useState([]);

  const token = TokenStorage.getToken();

  let tokenDetails = {};

  try {
    tokenDetails = token ? jwt(token) : {};
  } catch (error) {
    tokenDetails = {};
  }

  const loginUserId = useSelector((state) => {
    return state?.OAuth?.authData?.user_uid || "";
  });

  const loginUserDetails = useSelector((state) => {
    const authData = state?.OAuth?.authData?.user_name || "";

    const firstName = state?.OAuth?.loginUserData?.firstName;
    const lastName = state?.OAuth?.loginUserData?.lastName;

    if (firstName !== undefined && firstName !== "") {
      return `${firstName || ""} ${lastName || ""}`.trim();
    }

    return authData;
  });

  const loginRole = useSelector((state) => {
    const role = state?.OAuth?.authData?.role || "Admin";
    return role.replace(/_/g, " ");
  });

  let sidebarOptions = [];

  if (window._appConstants?.menuOptions?.length) {
    window._appConstants.menuOptions.forEach((element, index) => {
      if (index === 0) {
        sidebarOptions.push(element);
      }

      const tileRoles =
        element.roles || (element.role ? [element.role] : []);

      if (tileRoles.some((r) => tokenDetails?.authorities?.includes(r))) {
        element.id = sidebarOptions.length;
        sidebarOptions.push(element);
      }
    });
  }

  const icon = sidebarOptions;

  const gradients = [
    "linear-gradient(135deg, #1D4ED8 0%, #3B82F6 100%)",
    "linear-gradient(135deg, #6D28D9 0%, #8B5CF6 100%)",
    "linear-gradient(135deg, #0E7490 0%, #06B6D4 100%)",
    "linear-gradient(135deg, #047857 0%, #10B981 100%)",
    "linear-gradient(135deg, #B45309 0%, #F59E0B 100%)",
    "linear-gradient(135deg, #B91C1C 0%, #EF4444 100%)",
    "linear-gradient(135deg, #9D174D 0%, #EC4899 100%)",
    "linear-gradient(135deg, #C2410C 0%, #F97316 100%)",
    "linear-gradient(135deg, #0F766E 0%, #14B8A6 100%)",
    "linear-gradient(135deg, #3730A3 0%, #6366F1 100%)",
    "linear-gradient(135deg, #7E22CE 0%, #A855F7 100%)",
  ];

  const getStoredSelectedMake = () => {
    try {
      if (typeof TokenStorage.getCurrentMakeSelected !== "function") {
        return {
          uid: "",
          name: "",
        };
      }

      const rawSelectedMake = TokenStorage.getCurrentMakeSelected();

      if (!rawSelectedMake) {
        return {
          uid: "",
          name: "",
        };
      }

      if (typeof rawSelectedMake === "string") {
        return JSON.parse(rawSelectedMake);
      }

      return rawSelectedMake;
    } catch (error) {
      return {
        uid: "",
        name: "",
      };
    }
  };

  const getTileGradient = (value, index) => {
    if (value.color1 && value.color2) {
      return `linear-gradient(135deg, ${value.color1} 0%, ${value.color2} 100%)`;
    }

    return gradients[index % gradients.length];
  };

  const getTileTag = (value) => {
    const title = value?.title?.toLowerCase() || "";
    const subTitle = value?.subTitle?.toLowerCase() || "";

    if (
      // title.includes("user") ||
      title.includes("role") ||
      title.includes("policy") ||
      title.includes("oem")
    ) {
      return "Admin";
    }

    if (
      title.includes("new inspection")
      // subTitle.includes("inspection") ||
      // title.includes("job")
    ) {
      return "Inspection";
    }

    if (
      title.includes("dashboard") ||
      title.includes("report") ||
      subTitle.includes("report")
    ) {
      return "Analytics";
    }

    return "Operation";
  };

  const getInitial = () => {
    if (!loginUserDetails) return "A";
    return loginUserDetails.trim().charAt(0).toUpperCase();
  };

  const getMenuIconSrc = (value) => {
    try {
      if (!value?.icon) return null;

      return require(`../../assets/images/svg/${value.icon}.svg`).default;
    } catch (error) {
      return null;
    }
  };

  useEffect(() => {
    try {
      const selectedMake = getStoredSelectedMake();

      if (selectedMake && selectedMake.uid) {
        setMakeSelected(selectedMake);
      }

      const make = TokenStorage.getMake();

      dispatch(OAuthActions.getOemDashboard(make)).then(function (flag) {
        setMakeList(flag || []);
      });

      let finalLoginUserId = loginUserId;

      if (token && finalLoginUserId === "") {
        const cookieData = TokenStorage.getMultiTabData();
        finalLoginUserId = cookieData?.user_uid || "";
      }

      dispatch(OAuthActions.getAllRole()).then(function () { });

      if (finalLoginUserId) {
        dispatch(OAuthActions.getLoginUser(finalLoginUserId)).then(function () { });
      }
    } catch (error) {
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }
  }, []);

  const handleTileClick = async (event, value) => {
    const isMakeNotSelected = !makeSelected?.uid;

    if (isMakeNotSelected) {
      event.preventDefault();

      dispatch(
        setSnackbar(
          true,
          "info",
          "Please select an OEM before starting to utilize the application's features."
        )
      );

      return;
    }

    await dispatch(OAuthActions.ClickHandler(value.id));
  };

  return (
    <Box className={classes.root}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
          paddingBottom: 20,
        }}
      >
        <Box className={classes.userBlock} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Avatar className={classes.avatar}>{getInitial()}</Avatar>

          <Box>
            <Typography className={classes.welcomeText}>
              Welcome {loginUserDetails || "Admin"}!
            </Typography>

            <Chip label={loginRole} size="small" className={classes.roleChip} />
          </Box>
        </Box>

        {makeList.length >= 1 && makeList && loginRole !== "UTILITY ADMIN" ? (
          <Box>
            <Autocomplete
              fullWidth
              disableClearable
              size="small"
              id="model-select"
              placeholder="Select OEM"
              value={makeList.find((m) => m.uid === makeSelected?.uid) || null}
              options={makeList}
              getOptionLabel={(option) => option.name || ""}
              getOptionSelected={(option, value) => option.uid === value?.uid}
              onChange={(event, newValue) => {
                if (!newValue) {
                  const val = {
                    uid: "",
                    name: "Not Selected",
                  };
                  setMakeSelected(val);
                  TokenStorage.setCurrentMakeSelected(val);
                } else {
                  setMakeSelected(newValue);
                  TokenStorage.setCurrentMakeSelected(newValue);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  variant="outlined"
                  className={classes.oemSelect}
                  id="model-select"
                  placeholder="Select OEM"
                  InputProps={{
                    ...params.InputProps,
                  }}
                />
              )}
              PaperComponent={({ children }) => (
                <div className={classes.autocompletePaper}>{children}</div>
              )}
              renderOption={(option) => (
                <MenuItem
                  className={classes.menuItem}
                  key={option.uid}
                  value={option.uid}
                  style={{
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    display: "block",
                  }}
                >
                  {option.name}
                </MenuItem>
              )}
            />
          </Box>
        ) : null}
      </Box>

      {icon.length > 1 ? (
        <Grid container spacing={3}>
          {icon.map((value, index) => {
            if (value.id === 0) return null;

            const gradient = getTileGradient(value, index);
            const disabled = !makeSelected?.uid;
            const iconSrc = getMenuIconSrc(value);

            return (
              <Grid key={value.id} item xs={12} sm={6} md={3} lg={3}>
                <Link
                  to={disabled ? "" : value.href}
                  className={classes.tileLink}
                  onClick={(event) => handleTileClick(event, value)}
                >
                  <Paper
                    elevation={0}
                    className={classes.card}
                  >
                    <Box
                      className={classes.gradientZone}
                      style={{ background: gradient }}
                    >
                      <Typography className={classes.tagPill}>
                        {getTileTag(value)}
                      </Typography>

                      <Box className={classes.iconBox}>
                        {iconSrc ? (
                          <img
                            src={iconSrc}
                            className={classes.tileIcon}
                            alt={value.title}
                          />
                        ) : (
                          <Typography className={classes.fallbackIconText}>
                            {value?.title
                              ? value.title.charAt(0).toUpperCase()
                              : "M"}
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    <Box className={classes.bottomContent}>
                      <Typography className={classes.tileTitle}>
                        {value.title}
                      </Typography>

                      <Typography className={classes.tileDescription}>
                        {value.subTitle}
                      </Typography>
                    </Box>
                  </Paper>
                </Link>
              </Grid>
            );
          })}
        </Grid>
      ) : (
        <Box className={classes.downloadContainer}>
          <Box className={classes.downloadBox}>
            <CloudDownloadIcon
              className={classes.downloadIcon}
              onClick={async () => {
                await dispatch(OAuthActions.downloadAPK());
              }}
            />

            <Typography
              variant="h5"
              style={{
                color: "#0F172A",
                fontWeight: 600,
                fontSize: 16,
              }}
            >
              To download the Technician App, click on the download icon.
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};
export default Dashboard;